import { useCallback, useRef, useState } from "react";

/**
 * Types of content that can be copied to the clipboard
 */
export enum CopyContentType {
  /** Plain text content */
  TEXT = "text",
  /** HTML content (tables, formatted text, etc.) */
  HTML = "html",
}

/**
 * Options for the useCopyToClipboard hook
 */
export interface CopyToClipboardOptions {
  /**
   * The type of content to copy
   * @default CopyContentType.TEXT
   */
  contentType?: CopyContentType;

  /**
   * Duration in milliseconds that the isCopied state will remain true after copying
   * @default 2000
   */
  copiedStateDuration?: number;

  /**
   * Custom function to extract HTML content to be copied
   * Useful when you need specialized HTML extraction logic
   */
  customHtmlExtractor?: () => string | null;

  /**
   * Custom function to extract plain text content to be copied
   * Useful when you need specialized text extraction logic
   */
  customTextExtractor?: () => string | null;

  /**
   * Whether to enable fallback to plain text when HTML copying fails
   * @default true
   */
  enableFallback?: boolean;
}

/**
 * Result object returned by the useCopyToClipboard hook
 */
export interface CopyToClipboardResult {
  /**
   * Reference to attach to the element containing content to be copied
   */
  targetRef: React.RefObject<HTMLDivElement>;

  /**
   * Whether content was successfully copied (resets after copiedStateDuration)
   */
  isCopied: boolean;

  /**
   * Function to trigger the copy operation
   */
  copy: () => Promise<boolean>;

  /**
   * Any error that occurred during the last copy operation
   */
  error: Error | null;
}

/**
 * Hook for copying content to clipboard with support for formatted HTML and plain text
 *
 * This hook provides a flexible way to copy content from a referenced DOM element to the clipboard,
 * with support for preserving formatting when needed (like tables or rich text).
 *
 * Features:
 * - Copy as HTML to preserve formatting (tables, styling, etc.)
 * - Copy as plain text with intelligent line break handling
 * - Fallback to plain text if HTML copying fails
 * - Custom content extractors for specialized use cases
 * - Error tracking and handling
 *
 * @example
 * // Basic usage - copy as plain text
 * const { targetRef, isCopied, copy } = useCopyToClipboard();
 *
 * @example
 * // Copy as HTML (e.g., for tables)
 * const { targetRef, isCopied, copy } = useCopyToClipboard({
 *   contentType: CopyContentType.HTML
 * });
 *
 * @example
 * // With custom extractor and longer success indicator
 * const { targetRef, isCopied, copy } = useCopyToClipboard({
 *   contentType: CopyContentType.HTML,
 *   copiedStateDuration: 3500,
 *   customHtmlExtractor: () => {
 *     // Custom logic to generate HTML
 *     return myCustomTable.innerHTML;
 *   }
 * });
 *
 * @param {CopyToClipboardOptions} options - Configuration options
 * @returns {CopyToClipboardResult} Result object with refs and methods
 */
export const useCopyToClipboard = (
  options: CopyToClipboardOptions = {},
): CopyToClipboardResult => {
  // Destructure options with defaults
  const {
    contentType = CopyContentType.TEXT,
    copiedStateDuration = 2000,
    customHtmlExtractor,
    customTextExtractor,
    enableFallback = true,
  } = options;

  // State for tracking copy status and errors
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Reference to the element containing content to copy
  const targetRef = useRef<HTMLDivElement | null>(null);

  /**
   * Extracts plain text from an element with intelligent line break handling.
   * Traverses the DOM tree and inserts line breaks after block-level elements.
   *
   * @param element - The HTML element to extract text from
   * @returns Formatted plain text with appropriate line breaks
   */
  const extractTextWithLineBreaks = useCallback(
    (element: HTMLElement): string => {
      // Use TreeWalker API for efficient DOM traversal
      const walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
      );

      let text = "";
      const blockElements = [
        "address",
        "article",
        "aside",
        "blockquote",
        "canvas",
        "dd",
        "div",
        "dl",
        "dt",
        "fieldset",
        "figcaption",
        "figure",
        "footer",
        "form",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "header",
        "hr",
        "li",
        "main",
        "nav",
        "noscript",
        "ol",
        "p",
        "pre",
        "section",
        "table",
        "tfoot",
        "ul",
        "video",
        "tr",
        "br",
      ];

      while (walker.nextNode()) {
        const node = walker.currentNode;

        if (node.nodeType === Node.TEXT_NODE) {
          // Add text content with a trailing space to separate inline elements
          const textContent = (node as Text).data.trim();
          if (textContent) {
            text += textContent + " ";
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const tag = (node as HTMLElement).tagName.toLowerCase();

          // Add line break after block-level elements
          if (blockElements.includes(tag)) {
            text = text.trimEnd() + "\n";
          }
        }
      }

      // Normalize line breaks (remove multiple consecutive line breaks)
      return text.replace(/\n{2,}/g, "\n").trim();
    },
    [],
  );

  /**
   * Extracts HTML content from the target element.
   * Uses customHtmlExtractor if provided, otherwise tries to find specific elements.
   *
   * @returns HTML string or null if extraction failed
   */
  const extractHTML = useCallback((): string | null => {
    // Use custom extractor if provided
    if (customHtmlExtractor) {
      return customHtmlExtractor();
    }

    const el = targetRef.current;
    if (!el) return null;

    // Prioritize tables for better formatted data copying
    const table = el.querySelector("table");
    if (table) {
      return table.outerHTML;
    }

    // Fall back to the entire content
    return el.innerHTML;
  }, [customHtmlExtractor]);

  /**
   * Extracts plain text content from the target element.
   * Uses customTextExtractor if provided, otherwise extracts with line breaks.
   *
   * @returns Plain text string or null if extraction failed
   */
  const extractText = useCallback((): string | null => {
    // Use custom extractor if provided
    if (customTextExtractor) {
      return customTextExtractor();
    }

    const el = targetRef.current;
    if (!el) return null;

    return extractTextWithLineBreaks(el);
  }, [customTextExtractor, extractTextWithLineBreaks]);

  /**
   * Writes plain text content to the clipboard.
   *
   * @param text - The text content to write to clipboard
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const writeTextToClipboard = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text);
        setIsCopied(true);
        setError(null);
        setTimeout(() => setIsCopied(false), copiedStateDuration);
        return true;
      } catch (err) {
        console.error("Failed to copy text content:", err);
        setError(err instanceof Error ? err : new Error(String(err)));
        return false;
      }
    },
    [copiedStateDuration],
  );

  /**
   * Writes HTML content to the clipboard using the modern Clipboard API.
   * Also includes a plain text version as fallback for apps that don't support HTML.
   *
   * @param html - The HTML content to write to clipboard
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const writeHTMLToClipboard = useCallback(
    async (html: string): Promise<boolean> => {
      try {
        // Create blobs for both HTML and plain text versions
        const htmlBlob = new Blob([html], { type: "text/html" });

        // Prefer the line-break-aware DOM/text extractor so the plain-text
        // representation keeps paragraph structure. Fall back to a naive
        // tag-strip only when no target/extractor is available (e.g. HTML
        // built purely via customHtmlExtractor with no targetRef).
        const plainText =
          extractText() ??
          html
            .replace(/<[^>]+>/g, " ")
            .replace(/\s{2,}/g, " ")
            .trim();
        const textBlob = new Blob([plainText], { type: "text/plain" });

        // Create a ClipboardItem with multiple representations
        const clipboardItem = new ClipboardItem({
          "text/html": htmlBlob,
          "text/plain": textBlob,
        });

        // Write to clipboard
        await navigator.clipboard.write([clipboardItem]);
        setIsCopied(true);
        setError(null);
        setTimeout(() => setIsCopied(false), copiedStateDuration);
        return true;
      } catch (err) {
        console.error("Failed to copy HTML content:", err);
        setError(err instanceof Error ? err : new Error(String(err)));

        // Attempt fallback if enabled
        if (enableFallback) {
          const text = extractText();
          if (text) {
            return writeTextToClipboard(text);
          }
        }
        return false;
      }
    },
    [copiedStateDuration, enableFallback, extractText, writeTextToClipboard],
  );

  /**
   * Main copy function triggered by user action.
   * Determines whether to copy as HTML or text based on contentType option.
   *
   * @returns Promise that resolves to true if copying was successful
   */
  const copy = useCallback(async (): Promise<boolean> => {
    // Reset previous errors
    setError(null);

    // Check for Clipboard API support
    if (!navigator.clipboard) {
      const err = new Error("Clipboard API is not supported in this browser");
      setError(err);
      console.error(err.message);
      return false;
    }

    if (contentType === CopyContentType.HTML) {
      const html = extractHTML();
      if (!html) {
        const err = new Error("No HTML content found to copy");
        setError(err);
        return false;
      }
      return writeHTMLToClipboard(html);
    } else {
      const text = extractText();
      if (!text) {
        const err = new Error("No text content found to copy");
        setError(err);
        return false;
      }
      return writeTextToClipboard(text);
    }
  }, [
    contentType,
    extractHTML,
    extractText,
    writeHTMLToClipboard,
    writeTextToClipboard,
  ]);

  return { targetRef, isCopied, copy, error };
};

export default useCopyToClipboard;
